import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import { WorkItemBase } from 'common/workitems/models/workitem-base-model';
import { Chat } from 'common/xislivechat/models/chat';
import { ChatItem } from 'common/workitems/interfaces/chat-item.interface';
import { UserPermissionService } from 'shared/services/user-permissions/user-permission.service';
import { ActionPermissionType } from 'common/common/constants/permission-type-enum';
import { BrowserSupportService } from 'shared/services/browser-support/browser-support.service';
import { RemoteControlService } from 'shared/services/remote-control/remote-control.service';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { BcAnalytics } from 'common/analytics/analytics.service';
import { BcRestApi } from 'common/api/rest-api';
import { WorkAreaManager } from 'common/workitems/work-area-manager.service';
import { WorkItemListService } from 'common/workitems/workitem-list.service';
import { BcConfigService } from 'common/config/provider';
import { BcSettingsService } from 'common/settings/settings.service';
import { BcFeatures } from 'common/common/constants/feature-enums';
import { VideoService } from 'shared/services/video-service/video.service';
import { VideoSessionStatus } from 'common/common/constants/video-session-status';
import { bcEnums, AssigneeTypes } from 'common/common/constants/boldchat-enums';
import { ChatWindowSettings } from 'common/api/models/chat-window-settings.model';
import { ChatWindowMode } from 'common/api/models/chat-window-mode.model';
import { ActiveAssistStatus } from 'common/common/constants/active-assist-status';

@Component({
  selector: 'work-widget-header',
  templateUrl: './work-widget-header.component.html',
  styleUrls: ['./work-widget-header.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class WorkWidgetHeaderComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  workItem: WorkItemBase | ChatItem;

  @Input()
  title: string;

  @Input()
  visitor: any;

  @Input()
  previewMode: boolean;

  @Input()
  showButtons: boolean = true;

  @Input()
  chat: Chat;

  @Output()
  closeAction = new EventEmitter<void>();

  selfCollapsed;
  isRcFeatureAvailable = false;
  isAbFeatureAvailable = false;
  isVendorRemoteControlEnabled = false;

  isVideoFeatureEnabled = false;
  videoBeingInitiated = false;
  isConfirmationVisible = false;
  private readonly chatWindowSettingsSubject: BehaviorSubject<ChatWindowSettings> = new BehaviorSubject(null);

  private _isRemoteControlBeingInitiated = false;

  private _visitorInfoToggleSubscription: Subscription;
  private _chatWindowSettingsSubscription: Subscription;
  private _enforceStartVideoCallSubscription: Subscription;
  private _autoStartVideoCallSubscription: Subscription;
  private _enforceAutoStartVideoCallSubscription: Subscription;
  private _confirmationVisibilityChangedSubscription: Subscription;
  private _ignoreAutoStartVideoCallSubscription: Subscription;
  private _cancelStartVideoCallSubscription: Subscription;
  private _videoConfirmationVisibilityChangedSubscription: Subscription;
  private _videoAutoStartConfirmationVisibilityChangedSubscription: Subscription;
  private _videoWithAcdConfirmationVisibilityChanged: Subscription;

  constructor(
    private readonly _config: BcConfigService,
    private readonly _workItemList: WorkItemListService,
    private readonly _workAreaManager: WorkAreaManager,
    private readonly _userPermissionService: UserPermissionService,
    private readonly _bcSettingsService: BcSettingsService,
    private readonly _browserSupportService: BrowserSupportService,
    private readonly _bcAnalytics: BcAnalytics,
    private readonly _bcRestApi: BcRestApi,
    private readonly _remoteControlService: RemoteControlService,
    private readonly _videoService: VideoService,
    private readonly _rxEmitter: RxEmitter
  ) {
    this.selfCollapsed = true;
    this._chatWindowSettingsSubscription = this.chatWindowSettingsSubject.subscribe((chatWindowSettings) =>
      this._onVideoAutoStartChange(chatWindowSettings?.videoAutoStart)
    );
  }

  ngOnInit() {
    this._loadFeatureValues().then(() => this._getChatWindowSettings());

    this._visitorInfoToggleSubscription = this._rxEmitter.subscribe(
      `${MessageBusEvents.ToggleVisitorInformationByWorkItemId}${this.workItem.id}`,
      () => {
        this.toggleSelfCollapsed();
      }
    );

    this._enforceStartVideoCallSubscription = this._rxEmitter.subscribe(
      `${MessageBusEvents.EnforceStartVideoCall}${this.workItem.id}`,
      () => {
        this._enforceStartVideoCall();
      }
    );

    this._autoStartVideoCallSubscription = this._rxEmitter.subscribe(
      `${MessageBusEvents.AutoStartVideoCall}${this.workItem.id}`,
      () => {
        this._autoStartVideoCall();
      }
    );

    this._enforceAutoStartVideoCallSubscription = this._rxEmitter.subscribe(
      `${MessageBusEvents.EnforceAutoStartVideoCall}${this.workItem.id}`,
      () => {
        this._enforceAutoStartVideoCall();
      }
    );

    this._ignoreAutoStartVideoCallSubscription = this._rxEmitter.subscribe(
      `${MessageBusEvents.IgnoreAutoStartVideoCall}${this.workItem.id}`,
      () => {
        this._ignoreAutoStartVideoCall();
      }
    );

    this._cancelStartVideoCallSubscription = this._rxEmitter.subscribe(
      `${MessageBusEvents.CancelStartVideoCall}${this.workItem.id}`,
      () => {
        this._cancelStartVideoCall();
      }
    );

    this._videoConfirmationVisibilityChangedSubscription = this._rxEmitter.subscribe(
      `${MessageBusEvents.VideoConfirmationVisibilityChanged}${this.workItem.id}`,
      (isVisible: boolean) => {
        this.isConfirmationVisible = isVisible;
      }
    );

    this._videoAutoStartConfirmationVisibilityChangedSubscription = this._rxEmitter.subscribe(
      `${MessageBusEvents.VideoAutoStartConfirmationVisibilityChanged}${this.workItem.id}`,
      (isVisible: boolean) => {
        this.isConfirmationVisible = isVisible;
      }
    );

    this._videoWithAcdConfirmationVisibilityChanged = this._rxEmitter.subscribe(
      `${MessageBusEvents.VideoWithAcdConfirmationVisibilityChanged}${this.workItem.id}`,
      (isVisible: boolean) => {
        this.isConfirmationVisible = isVisible;
      }
    );
  }

  ngOnChanges() {
    this._loadFeatureValues();
  }

  ngOnDestroy() {
    this._safeUnsubscribe(this._visitorInfoToggleSubscription);
    this._safeUnsubscribe(this._chatWindowSettingsSubscription);
    this._safeUnsubscribe(this._enforceStartVideoCallSubscription);
    this._safeUnsubscribe(this._autoStartVideoCallSubscription);
    this._safeUnsubscribe(this._enforceAutoStartVideoCallSubscription);
    this._safeUnsubscribe(this._ignoreAutoStartVideoCallSubscription);
    this._safeUnsubscribe(this._cancelStartVideoCallSubscription);
    this._safeUnsubscribe(this._confirmationVisibilityChangedSubscription);
    this._safeUnsubscribe(this._videoConfirmationVisibilityChangedSubscription);
    this._safeUnsubscribe(this._videoAutoStartConfirmationVisibilityChangedSubscription);
    this._safeUnsubscribe(this._videoWithAcdConfirmationVisibilityChanged);
  }

  async initiateVideoCall(isVideoAutoStart = false): Promise<void> {
    this.videoBeingInitiated = true;
    const enforced = false;
    const success = await this._videoService.startVideoCall(this.chat, enforced, isVideoAutoStart);
    if (!success) {
      if (isVideoAutoStart) {
        this._showVideoWithAcdConfirmation();
      } else {
        this._showVideoConfirmation();
      }
    }
    this.videoBeingInitiated = false;
  }

  get isChatExpanded() {
    return this._isChat() && this.isExpandedMode();
  }

  get isTextChatExpanded() {
    return this.isTextChat() && this.isExpandedMode();
  }

  get isEmailExpanded() {
    return this._isEmail() && this.isExpandedMode();
  }

  get canInitiateVideo(): boolean {
    return (
      this.isVideoFeatureEnabled &&
      VideoSessionStatus.hasValue(this.chat.VideoSessionStatus) &&
      !VideoSessionStatus.isInSession(this.chat.VideoSessionStatus) &&
      this.chat.VideoSessionStatus !== VideoSessionStatus.Uninitialized &&
      this.chat.isAlive() &&
      !this.isConfirmationVisible
    );
  }

  get canAcceptVideo(): boolean {
    return (
      this.isVideoFeatureEnabled &&
      this.chat.VideoSessionStatus === VideoSessionStatus.InitiatedByVisitor &&
      this.chat.isAlive()
    );
  }

  getIconClass() {
    switch (this.workItem.type) {
      case WorkItemTypes.EMAIL:
      case WorkItemTypes.COMPOSE_EMAIL:
        return 'mail-open';
      case WorkItemTypes.CHAT:
      case WorkItemTypes.CHAT_PIN_INVITE:
      case WorkItemTypes.SMS:
      case WorkItemTypes.FACEBOOK:
      case WorkItemTypes.ASYNC_MESSAGE:
      case WorkItemTypes.DISCUSSION:
        return 'boldchat';
      default:
        throw new Error('Unknown header type.');
    }
  }

  getCollapseIconClass() {
    return !this.isExpandedMode() ? 'expand-arrows' : 'collapse-arrows';
  }

  isExpandedMode() {
    return !this.previewMode && (this._workAreaManager.isExpandedMode() || !this.workItem.isCollapsible);
  }

  private _isChat(): boolean {
    return this.workItem.type === WorkItemTypes.CHAT;
  }

  public isTextChat(): boolean {
    return this.isFacebook() || this._isSms() || this.isAsyncMessage();
  }

  private _isSms(): boolean {
    return this.workItem.type === WorkItemTypes.SMS;
  }

  public isFacebook(): boolean {
    return this.workItem.type === WorkItemTypes.FACEBOOK;
  }

  public isAsyncMessage(): boolean {
    return this.workItem.type === WorkItemTypes.ASYNC_MESSAGE;
  }

  public isDiscussion(): boolean {
    return this.workItem.type === WorkItemTypes.DISCUSSION;
  }

  private _isEmail(): boolean {
    return this.workItem.type === WorkItemTypes.EMAIL;
  }

  isPinInvite(): boolean {
    return this.workItem.type === WorkItemTypes.CHAT_PIN_INVITE;
  }

  isChatOrPinvite(): boolean {
    return this._isChat() || this.isPinInvite();
  }

  toggleExpandedMode() {
    this._bcAnalytics.eventTrack('SwitchedView', { type: this.isExpandedMode() ? 'ExpandedMode' : 'CollapsedMode' });
    this._rxEmitter.send(MessageBusEvents.ToggleExpandedMode, null);

    if (this.isExpandedMode() && !this.isSelfCollapsed()) {
      this.toggleSelfCollapsed();
    }

    this.focusWorkItem();
  }

  toggleSelfCollapsed() {
    this.selfCollapsed = !this.selfCollapsed;
  }

  focusWorkItem() {
    if (!this.previewMode) {
      this._workItemList.focusWorkItem(this.workItem);
    }
  }

  isSelfCollapsed() {
    return this.selfCollapsed;
  }

  hideLane() {
    this._bcAnalytics.eventTrack('MinimizeWorkItem');
    this._workAreaManager.removeWorkItem(this.workItem.id);
  }

  closeLane() {
    this.closeAction.emit();
  }

  async startRCSession() {
    if (this.isVendorRemoteControlEnabled) {
      this._isRemoteControlBeingInitiated = true;
      await this._remoteControlService.requestRemoteControlSession(this.workItem.id);
      this._isRemoteControlBeingInitiated = false;
    } else {
      (this.workItem as ChatItem).startRCSession();
    }
    this._bcAnalytics.eventTrack('StartRCSession', { from: 'Chat' });
  }

  startAssistedBrowsing() {
    (<ChatItem>this.workItem).startAssistedBrowsing();
    this._bcAnalytics.eventTrack('StartAssistedBrowsing', { from: 'Chat' });
  }

  hasActiveRemoteControl() {
    return (<ChatItem>this.workItem).hasActiveRemoteControl;
  }

  hasActiveAssistedBrowsing() {
    return (<ChatItem>this.workItem).hasActiveAssistedBrowsing;
  }

  getRemoteControlIcon() {
    return this.hasActiveRemoteControl() && !this.isVendorRemoteControlEnabled
      ? 'remote-access'
      : 'remote-access-inactive';
  }

  getAssistedBrowsingIcon() {
    return this.hasActiveAssistedBrowsing() ? 'assisted-browsing' : 'assisted-browsing-inactive';
  }

  isAssistedSessionAllowed() {
    return this.isChatExpanded && !(<ChatItem>this.workItem).isEnded;
  }

  private _showVideoAutoStartConfirmation(): void {
    this._rxEmitter.send(`${MessageBusEvents.VideoAutoStartConfirmationVisibilityChanged}${this.workItem.id}`, true);
  }

  private _showVideoConfirmation(): void {
    this._rxEmitter.send(`${MessageBusEvents.VideoConfirmationVisibilityChanged}${this.workItem.id}`, true);
  }

  private _showVideoWithAcdConfirmation(): void {
    this._rxEmitter.send(`${MessageBusEvents.VideoWithAcdConfirmationVisibilityChanged}${this.workItem.id}`, true);
  }

  private _autoStartVideoCall(): void {
    this._rxEmitter.send(`${MessageBusEvents.VideoAutoStartConfirmationVisibilityChanged}${this.workItem.id}`, false);
    this.initiateVideoCall(true);
  }

  private _enforceAutoStartVideoCall(): void {
    this._rxEmitter.send(`${MessageBusEvents.VideoWithAcdConfirmationVisibilityChanged}${this.workItem.id}`, false);
    this._videoService.startVideoCall(this.chat, true, true);
  }

  private _enforceStartVideoCall(): void {
    this._rxEmitter.send(`${MessageBusEvents.VideoConfirmationVisibilityChanged}${this.workItem.id}`, false);
    this._videoService.startVideoCall(this.chat, true, false);
  }

  private _ignoreAutoStartVideoCall(): void {
    this._rxEmitter.send(`${MessageBusEvents.VideoConfirmationVisibilityChanged}${this.workItem.id}`, false);
    this._rxEmitter.send(`${MessageBusEvents.VideoWithAcdConfirmationVisibilityChanged}${this.workItem.id}`, false);
    this._bcRestApi.assignChat(this.chat.ChatID, AssigneeTypes.ACD, AssigneeTypes.ACD);
  }

  private _cancelStartVideoCall(): void {
    this._rxEmitter.send(`${MessageBusEvents.VideoConfirmationVisibilityChanged}${this.workItem.id}`, false);
  }

  private _getChatWindowSettings(): void {
    if ((this.isVideoFeatureEnabled || this.isVendorRemoteControlEnabled) && this.chat) {
      this._bcRestApi.getChatWindowSettings(this.chat.ChatWindowDefID).then((chatWindowSettings) => {
        this.chatWindowSettingsSubject.next(chatWindowSettings);
      });
    }
  }

  private _onVideoAutoStartChange(isVideoAutoStart: boolean): void {
    if (!isVideoAutoStart || !this.canAcceptVideo) {
      return;
    }
    this._showVideoAutoStartConfirmation();
  }

  private _safeUnsubscribe(sub: Subscription): void {
    if (sub) {
      sub.unsubscribe();
      sub = null;
    }
  }

  private async _loadFeatureValues(): Promise<void> {
    const [isVendorRemoteControlEnabled, isVideoFeatureEnabled] = await Promise.all([
      this._getIsVendorRemoteControlEnabled(),
      this._isVideoFeatureEnabled()
    ]);
    this.isVendorRemoteControlEnabled = isVendorRemoteControlEnabled;
    this.isVideoFeatureEnabled = isVideoFeatureEnabled;
    this.isAbFeatureAvailable = this._isAssistedBrowsingEnabled();
    this.isRcFeatureAvailable = this._isRemoteControlEnabled();
  }

  public isRemoteControlButtonDisabled(): boolean {
    if (this._isRemoteControlBeingInitiated) {
      return true;
    }

    if (this.hasActiveAssistedBrowsing()) {
      return true;
    }

    if (!this.isVendorRemoteControlEnabled && this._workItemList.isLiveSessionActive()) {
      return true;
    }

    if (this.isVendorRemoteControlEnabled) {
      const chatWindowSettings = this.chatWindowSettingsSubject.value;
      if (!chatWindowSettings) {
        return true;
      }

      if (
        this.chat.ClientType === bcEnums.visitorClientTypes.DESKTOP &&
        chatWindowSettings.desktopWindowMode === ChatWindowMode.POP_UP
      ) {
        return true;
      }

      if (
        this.chat.ClientType === bcEnums.visitorClientTypes.MOBILE &&
        chatWindowSettings.mobileWindowMode === ChatWindowMode.POP_UP
      ) {
        return true;
      }

      if (
        this.workItem.type === WorkItemTypes.CHAT &&
        (this.chat.ActiveAssistStatus === ActiveAssistStatus.Activated ||
          this.chat.ActiveAssistStatus === ActiveAssistStatus.Pending)
      ) {
        return true;
      }
    }
    return false;
  }

  public isAssistedBrowsingDisabled(): boolean {
    return this.hasActiveAssistedBrowsing() || this.hasActiveRemoteControl();
  }

  private _isRemoteControlEnabled(): boolean {
    return this.isVendorRemoteControlEnabled || this._isLegacyRemoteControlEnabled();
  }

  private async _getIsVendorRemoteControlEnabled(): Promise<boolean> {
    const settings = await this._bcSettingsService.getSettings();
    return (
      !!this.workItem && this._bcSettingsService.ChatFeatures.isEnabled(settings, BcFeatures.RC_NEW_REMOTE_CONTROL)
    );
  }

  private async _isVideoFeatureEnabled(): Promise<boolean> {
    const settings = await this._bcSettingsService.getSettings();
    return (
      this._bcSettingsService.ChatFeatures.isEnabled(settings, BcFeatures.LC_VIDEO_CHAT) &&
      this._bcSettingsService.ChatFeatures.isEnabled(settings, BcFeatures.LC_NEW_VIDEO_CHAT)
    );
  }

  private _isLegacyRemoteControlEnabled(): boolean {
    return (
      this._userPermissionService.hasPermissionFor(ActionPermissionType.RC_ENABLED) &&
      this._browserSupportService.isCmsLibSupportedByCurrentBrowser() &&
      !!this.workItem
    );
  }

  private _isAssistedBrowsingEnabled(): boolean {
    return (
      this._config &&
      this._config.isFeatureSwitchOn('assistedBrowsing') &&
      this._browserSupportService.isAssistedBrowsingSupportedByCurrentBrowser() &&
      this._userPermissionService.hasPermissionForActionTypes([
        ActionPermissionType.ASSISTED_BROWSING_ENABLED,
        ActionPermissionType.ASSISTED_BROWSING_ACTION
      ]) &&
      !!this.workItem
    );
  }
}
